import { useState, useEffect, FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { BiLoaderCircle } from "react-icons/bi";
import toast from "react-hot-toast";
import {
	PasswordResetCheckToken,
	PasswordResetRequest,
	PasswordResetUpdate
} from "@/legacy/ApiCallerOld";
import OtherFunctions from "../../Helpers/OtherFunctions";
import { AlertType } from "@shared/Components/AlertBox/AlertType";
import { AlertBox } from "@shared/Components/AlertBox/AlertBox";
import { validatePassword } from "@shared/Helpers/OtherFunctions";

export default function PasswordReset() {
	document.title = "Password Reset | GoDesk";

	const navigate = useNavigate();

	const [searchParams] = useSearchParams();

	const [reqInProgress, setReqInProgress] = useState(false);
	const [alertType, setAlertType] = useState<AlertType | undefined>();
	const [alertText, setAlertText] = useState("");
	const [isValidToken, setIsValidToken] = useState(false);
	const [isExpiredToken, setisExpiredToken] = useState(false);

	useEffect(() => {
		const token = searchParams.get("token");

		if (token != null) {
			checkToken(token);
		}

		async function checkToken(token: string) {
			const response = await PasswordResetCheckToken(token);

			if (response.successful && response?.data != null) {
				if (response.data == "expired") {
					setisExpiredToken(true);
					setAlertType("error");
					setAlertText(
						"The reset link has expired, please request a new password reset."
					);
				} else if (response.data == "invalidToken") {
					setIsValidToken(false);
					setAlertType("error");
					setAlertText("Invalid token.");
				} else if (response.data == "valid") {
					setIsValidToken(true);
				}
			}
		}
	}, []);

	async function handlePasswordUpdate(event: FormEvent) {
		event.preventDefault();
		setReqInProgress(true);

		const password = (
			document.getElementById("newPasswordInput") as HTMLInputElement
		)?.value;
		const confirmPassword = (
			document.getElementById("confirmPasswordInput") as HTMLInputElement
		)?.value;

		const token = searchParams.get("token");
		const isValidPass = validatePassword(
			password,
			confirmPassword
		);

		if (isValidPass != null) {
			setAlertType("error");
			setAlertText(isValidPass);
			setReqInProgress(false);
			return;
		}

		if (token != null) {
			const response = await PasswordResetUpdate(token, password);

			if (response.data == "success") {
				toast.success("Password changed!", { duration: 2000 });
				navigate("/login");
			} else if (response.data == "fail") {
				setAlertType("error");
				setAlertText("Could not send you a password reset email. Please contact GoDesk support at: support@getgodesk.com");
			}
		}

		setReqInProgress(false);
	}

	async function handleResetRequest(event: FormEvent) {
		event.preventDefault();
		setReqInProgress(true);

		const username =
			(document.getElementById("usernameInput") as HTMLInputElement)?.value ??
			"";

		const response = await PasswordResetRequest(username);

		if (response.successful) {
			if (response.data == "success") {
				setAlertType("info");
				setAlertText("Reset email sent.");
			} else if (response.data == "notfound") {
				setAlertType("error");
				setAlertText("Email does not exist.");
			}
		}

		setReqInProgress(false);
	}

	return (
		<div
			className="h-screen flex items-center justify-center pb-80"
			style={{ backgroundColor: "#E1F2FE" }}
		>
			<div className="max-w-md w-full mx-auto bg-gray-50 py-8 px-5 rounded-md shadow-md">
				<Link to="/login">
					<div className="w-2/3 mx-auto mb-6">
						<img src="/logowithtext.png" alt="GoDesk Logo" />
					</div>
				</Link>

				{alertType != null ? (
					<AlertBox alertType={alertType}>
						{alertText}
					</AlertBox>
				) : null}
				<h1 className="text-lg font-semibold mb-2">Password reset</h1>

				{isValidToken && !isExpiredToken ? (
					<>
						<ul
							className="mb-5"
							style={{ listStyleType: "circle", listStyle: "inside" }}
						>
							<li>Must be at least 8 characters long.</li>
							<li>Must contain no whitespaces.</li>
						</ul>

						<form className="" onSubmit={handlePasswordUpdate}>
							<label htmlFor="newPasswordInput">New password</label>
							<input
								id="newPasswordInput"
								type="password"
								placeholder="&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;"
								disabled={reqInProgress}
								className="mt-1 block w-full shadow-sm border-gray-300 rounded-md py-2 px-3 border focus:border-blue-500 mb-4"
								required
								autoComplete="new-password"
							/>

							<label htmlFor="confirmPasswordInput">Confirm password</label>
							<input
								id="confirmPasswordInput"
								type="password"
								placeholder="&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;"
								disabled={reqInProgress}
								className="mt-1 block w-full shadow-sm border-gray-300 rounded-md py-2 px-3 border focus:border-blue-500"
								required
								autoComplete="new-password"
							/>

							<div className="w-full flex flex-row justify-center">
								<button
									className="btn-blue mt-4 cursor-pointer"
									disabled={reqInProgress}
								>
									{reqInProgress && (
										<BiLoaderCircle className="animate-spin mr-2"></BiLoaderCircle>
									)}
									Confirm
								</button>
							</div>
						</form>
					</>
				) : (
					<>
						<form className="" onSubmit={handleResetRequest}>
							<label htmlFor="usernameInput">Email</label>
							<input
								id="usernameInput"
								type="email"
								placeholder="me@company.com"
								disabled={reqInProgress}
								className="mt-1 block w-full shadow-sm border-gray-300 rounded-md py-2 px-3 border focus:border-blue-500 mb-4"
								required
							/>

							<div className="w-full flex flex-row justify-center">
								<input
									className="btn-blue mt-4 cursor-pointer"
									type="submit"
									value="Send"
									disabled={reqInProgress}
								/>
							</div>
						</form>
					</>
				)}
			</div>
		</div>
	);
}
